import React, { useState, useRef } from "react";
import ConsentForm from "./ConsentForm";
import StepIndicator from "./StepIndicator";
import ComingSoonBadge from "./ComingSoonBadge";
import { uploadMedia } from "../../../features/media/utils/mediaUploadUtils";
import { CognitoAuthService } from "../../../services/cognitoService";
import { useAuth } from "../../../context/AuthContext";
import { debug } from "../../../utils/debugLogger";

interface AvatarCreationFormProps {
  idToken: string;
}

const AvatarCreationForm: React.FC<AvatarCreationFormProps> = ({ idToken }) => {
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [avatarVideo, setAvatarVideo] = useState<File | null>(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState<string>("");
  const [isUploading, setIsUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [recordedChunks, setRecordedChunks] = useState<Blob[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoPreviewRef = useRef<HTMLVideoElement>(null);
  const [consentVideoUrl, setConsentVideoUrl] = useState<string>("");

  const steps = [
    {
      number: 1,
      label: "Avatar Video",
      completed: currentStep > 1,
      current: currentStep === 1,
    },
    {
      number: 2,
      label: "Consent Verification",
      completed: currentStep > 2,
      current: currentStep === 2,
    },
  ];

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (file.type.startsWith("video/")) {
        setAvatarVideo(file);
        const url = URL.createObjectURL(file);
        setVideoPreviewUrl(url);
      } else {
        alert("Please upload a video file");
      }
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setAvatarVideo(file);
      const url = URL.createObjectURL(file);
      setVideoPreviewUrl(url);
    }
  };

  const startRecording = async () => {
    try {
      // Request camera and microphone access
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: "user",
        },
        audio: true,
      });

      setMediaStream(stream);
      
      // Set stream to video element for preview
      if (videoPreviewRef.current) {
        videoPreviewRef.current.srcObject = stream;
        videoPreviewRef.current.play();
      }

      // Create MediaRecorder
      const options = { mimeType: "video/webm;codecs=vp9" };
      let recorder: MediaRecorder;
      
      try {
        recorder = new MediaRecorder(stream, options);
      } catch (e) {
        // Fallback to default mimeType if vp9 is not supported
        recorder = new MediaRecorder(stream);
      }

      const chunks: Blob[] = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };

      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: "video/webm" });
        const file = new File([blob], `avatar-video-${Date.now()}.webm`, {
          type: "video/webm",
        });
        setAvatarVideo(file);
        const url = URL.createObjectURL(blob);
        setVideoPreviewUrl(url);
        setRecordedChunks([]);
      };

      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);
    } catch (error) {
      debug.error("Error accessing camera/microphone:", error);
      alert(
        "Unable to access camera/microphone. Please check your permissions and try again."
      );
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && isRecording) {
      mediaRecorder.stop();
      setIsRecording(false);
    }

    if (mediaStream) {
      mediaStream.getTracks().forEach((track) => track.stop());
      setMediaStream(null);
    }

    if (videoPreviewRef.current) {
      videoPreviewRef.current.srcObject = null;
    }
  };

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      if (mediaStream) {
        mediaStream.getTracks().forEach((track) => track.stop());
      }
      if (videoPreviewUrl) {
        URL.revokeObjectURL(videoPreviewUrl);
      }
    };
  }, [mediaStream, videoPreviewUrl]);

  const handleContinue = () => {
    if (!avatarVideo) {
      alert("Please record or upload an avatar video first");
      return;
    }
    setCurrentStep(2);
  };

  const handleConsentComplete = (videoUrl: string) => {
    setConsentVideoUrl(videoUrl);
    // After consent, proceed to upload avatar video
    handleUploadAvatarVideo();
  };

  const handleUploadAvatarVideo = async () => {
    if (!avatarVideo) {
      alert("Please record or upload an avatar video");
      return;
    }

    setIsUploading(true);
    try {
      const userEmail = CognitoAuthService.getUserEmail(idToken);
      // For now, we'll use a placeholder customer_id - this should be updated based on your requirements
      const customerId = "default"; // TODO: Get from user context or make it optional
      
      const result = await uploadMedia({
        file: avatarVideo,
        customerId,
        userEmail,
        token: idToken,
        isLogo: false,
        mediaType: "processed",
        additionalMetadata: {
          type: "avatar_training_video",
          consent_video_url: consentVideoUrl,
        },
        tags: ["avatar", "training"],
      });

      if (result.success) {
        alert("Avatar video uploaded successfully! Your avatar is being trained.");
        // Reset form
        setAvatarVideo(null);
        if (videoPreviewUrl) {
          URL.revokeObjectURL(videoPreviewUrl);
        }
        setVideoPreviewUrl("");
        setConsentVideoUrl("");
        setCurrentStep(1);
      } else {
        throw new Error(result.errorMessage || "Failed to upload avatar video");
      }
    } catch (error) {
      debug.error("Error uploading avatar video:", error);
      alert("Failed to upload avatar video. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleBackToVideo = () => {
    setCurrentStep(1);
  };

  return (
    <div className="space-y-8 font-sans">
      {/* Header Section */}
      <div className="flex items-center justify-between pb-6">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-800">Create New Avatar</h1>
            <ComingSoonBadge size="md" />
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Follow the steps to create your custom AI avatar. This feature is in beta.
          </p>
        </div>
      </div>
      
      {/* Beta Notice Banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <span className="material-icons text-amber-600">construction</span>
          <div className="flex-1">
            <p className="text-sm font-semibold text-amber-900 mb-1">
              Beta Feature - Under Active Development
            </p>
            <p className="text-sm text-amber-800">
              We're continuously improving this feature. Some advanced capabilities like video editing, 
              background removal, and enhanced avatar customization are coming soon. Your feedback helps us build better!
            </p>
          </div>
        </div>
      </div>

      {/* Step Indicator */}
      <StepIndicator steps={steps} />

      {/* Step 2: Consent Form */}
      {currentStep === 2 && (
        <ConsentForm
          idToken={idToken}
          onConsentComplete={handleConsentComplete}
          onBack={handleBackToVideo}
        />
      )}

      {/* Step 1: Avatar Video Upload */}
      {currentStep === 1 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Record Video Card */}
          <div className="flex flex-col bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
            <div className="p-6 flex-1 flex flex-col items-center justify-center text-center">
              <div className="relative flex items-center justify-center bg-slate-900 bg-cover bg-center aspect-video rounded-lg p-4 overflow-hidden w-full mb-4">
                {videoPreviewUrl || isRecording ? (
                  <video
                    ref={videoPreviewRef}
                    src={videoPreviewUrl || undefined}
                    controls={!!videoPreviewUrl && !isRecording}
                    className="w-full h-full object-contain rounded-lg"
                    muted
                    playsInline
                  />
                ) : (
                  <>
                    <div className="absolute inset-0 bg-black/30 rounded-lg"></div>
                    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 text-white">
                      <span className="material-icons text-5xl">videocam</span>
                      <p className="font-medium text-sm">Camera Preview</p>
                    </div>
                  </>
                )}
                {isRecording && (
                  <div className="absolute inset-x-0 bottom-0 px-4 py-3 z-10">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 rounded-full bg-black/30 px-3 py-1 text-xs font-medium text-white backdrop-blur-sm">
                        <div className="size-2 rounded-full bg-red-500 animate-pulse"></div>
                        <span>RECORDING</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          className="text-white p-1 rounded-full hover:bg-white/20 opacity-50 cursor-not-allowed"
                          disabled
                          title="Microphone controls coming soon"
                        >
                          <span className="material-icons text-lg">mic</span>
                        </button>
                        <button
                          className="text-white p-1 rounded-full hover:bg-white/20 opacity-50 cursor-not-allowed"
                          disabled
                          title="Camera settings coming soon"
                        >
                          <span className="material-icons text-lg">settings</span>
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex items-center justify-center gap-2 mb-2">
                <h2 className="text-lg font-semibold text-gray-800">Record Video</h2>
                <ComingSoonBadge size="sm" />
              </div>
              <p className="text-sm text-gray-500 mb-6">
                Record yourself speaking to create your avatar. Advanced camera controls coming soon.
              </p>
              {!isRecording ? (
                <button
                  onClick={startRecording}
                  className="flex w-full sm:w-auto cursor-pointer items-center justify-center gap-2 overflow-hidden rounded-lg h-11 bg-[#2f4941] hover:bg-[#1e3128] text-white text-sm font-bold leading-normal tracking-[0.015em] min-w-0 px-6 transition-colors"
                >
                  <span className="material-icons text-white text-xl">fiber_manual_record</span>
                  <span>Start Recording</span>
                </button>
              ) : (
                <button
                  onClick={stopRecording}
                  className="flex w-full sm:w-auto cursor-pointer items-center justify-center gap-2 overflow-hidden rounded-lg h-11 bg-red-600 hover:bg-red-700 text-white text-sm font-bold leading-normal tracking-[0.015em] min-w-0 px-6 transition-colors"
                >
                  <span className="material-icons text-white text-xl">stop</span>
                  <span>Stop Recording</span>
                </button>
              )}
            </div>
          </div>

          {/* Upload Video Card */}
          <div className="flex flex-col bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
            <div className="p-6 flex-1 flex flex-col items-center justify-center text-center">
              <div
                className={`flex flex-col items-center justify-center w-full h-full border-2 border-dashed rounded-lg bg-gray-50 p-6 mb-4 aspect-video transition-colors ${
                  dragActive
                    ? "border-[#2f4941] bg-[#2f4941]/5"
                    : "border-gray-300"
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                {videoPreviewUrl ? (
                  <div className="relative w-full h-full">
                    <video
                      ref={videoPreviewRef}
                      src={videoPreviewUrl}
                      controls
                      className="w-full h-full rounded-lg"
                    />
                    <button
                      onClick={() => {
                        setAvatarVideo(null);
                        if (videoPreviewUrl) {
                          URL.revokeObjectURL(videoPreviewUrl);
                        }
                        setVideoPreviewUrl("");
                      }}
                      className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    >
                      <span className="material-icons text-sm">close</span>
                    </button>
                  </div>
                ) : (
                  <>
                    <span className="material-icons text-5xl text-gray-400 mb-3">upload_file</span>
                    <p className="font-semibold text-gray-700">Drag & drop your video here</p>
                    <p className="text-sm text-gray-500">or click to browse</p>
                  </>
                )}
              </div>
              <h2 className="text-lg font-semibold text-gray-800 mb-2">Upload Video</h2>
              <p className="text-sm text-gray-500 mb-6">
                Upload an existing video to create your avatar.
              </p>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="flex w-full sm:w-auto cursor-pointer items-center justify-center gap-2 overflow-hidden rounded-lg h-11 bg-surface-ui hover:bg-surface-ui-hover text-gray-800 text-sm font-bold leading-normal tracking-[0.015em] min-w-0 px-6 transition-colors"
              >
                <span className="material-icons text-gray-700 text-xl">file_upload</span>
                <span>Upload Video</span>
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="video/*"
                onChange={handleFileChange}
                className="hidden"
              />
            </div>
          </div>
        </div>
      )}

      {/* Tips Section */}
      {currentStep === 1 && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">Tips for a Great Avatar</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="flex items-start gap-3">
              <span className="material-icons text-[#2f4941] text-xl mt-0.5">lightbulb</span>
              <div>
                <p className="font-semibold text-gray-700">Good Lighting</p>
                <p className="text-sm text-gray-500">
                  Ensure your face is well-lit and clearly visible.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="material-icons text-[#2f4941] text-xl mt-0.5">graphic_eq</span>
              <div>
                <p className="font-semibold text-gray-700">Speak Clearly</p>
                <p className="text-sm text-gray-500">
                  Speak at a natural pace without background noise.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="material-icons text-[#2f4941] text-xl mt-0.5">center_focus_strong</span>
              <div>
                <p className="font-semibold text-gray-700">Look at the Camera</p>
                <p className="text-sm text-gray-500">
                  Maintain eye contact for a more engaging result.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="material-icons text-[#2f4941] text-xl mt-0.5">wallpaper</span>
              <div>
                <p className="font-semibold text-gray-700">Clear Background</p>
                <p className="text-sm text-gray-500">
                  A simple, non-distracting background works best.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Continue Button */}
      {currentStep === 1 && (
        <div className="flex justify-end pt-2">
          <button
            onClick={handleContinue}
            disabled={!avatarVideo || isUploading}
            className="flex max-w-[480px] cursor-pointer items-center justify-center gap-3 overflow-hidden rounded-lg h-12 bg-[#2f4941] hover:bg-[#1e3128] text-white text-base font-bold leading-normal tracking-[0.015em] min-w-0 px-6 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed transition-colors"
          >
            <span>Next Step</span>
            <span className="material-icons">arrow_forward</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default AvatarCreationForm;

